import { useEffect, useState } from 'react'
import { sanitizeNumber } from '../utils/sanitizeInput'
import { useWalletStore } from '../store/walletStore'
import { useAuthStore } from '../store/authStore'
import { DollarSign, TrendingUp, TrendingDown, Clock, X, AlertCircle, CheckCircle2 } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import toast from 'react-hot-toast'

export default function Wallet() {
  const { balance, transactions, fetchBalance, fetchTransactions, createDeposit, requestDeposit, requestWithdrawal, loading } = useWalletStore()
  const { userData } = useAuthStore()
  const [clabeDeposits, setClabeDeposits] = useState([])
  const [clabeTransactions, setClabeTransactions] = useState([])
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false)
  const [depositAmount, setDepositAmount] = useState('')
  const [depositMethod, setDepositMethod] = useState('clabe')
  const [withdrawalAmount, setWithdrawalAmount] = useState('')
  const [clabeDestination, setClabeDestination] = useState('')
  const [beneficiaryName, setBeneficiaryName] = useState('')

  useEffect(() => {
    // Fetch CLABE deposits from Firestore
    const fetchClabeDepositsFirestore = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'))
        if (!user) return
        const { db } = await import('../services/firebase')
        const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore')
        const q = query(
          collection(db, 'paymentProofs'),
          where('userId', '==', user.uid),
          where('method', '==', 'clabe'),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        setClabeDeposits(data)
        setClabeTransactions(data.map(d => ({
          id: d.id,
          type: 'deposit',
          method: 'clabe',
          amount: d.amount,
          status: d.status,
          description: 'Depósito por transferencia CLABE',
          createdAt: d.createdAt?.toDate ? d.createdAt.toDate() : null,
          reference: d.reference || '',
          rejectionReason: d.rejectionReason || '',
        })))
      } catch (e) {
        setClabeDeposits([])
        setClabeTransactions([])
      }
    }
    // Fetch CLABE deposits via API (Mercado Pago)
    const fetchClabeDepositsAPI = async () => {
      const user = JSON.parse(localStorage.getItem('user'))
      if (!user) return
      const res = await fetch(`/api/clabeDeposits?uid=${user.uid}`)
      if (res.ok) {
        const data = await res.json()
        // No se mezclan, solo para Mercado Pago
        // setClabeDeposits(data)
      }
    }
    fetchClabeDepositsFirestore()
    fetchClabeDepositsAPI()
    fetchBalance()
    fetchTransactions()
  }, [fetchBalance, fetchTransactions])

  useEffect(() => {
    // Check for payment success/failure in URL
    const urlParams = new URLSearchParams(window.location.search)
    const payment = urlParams.get('payment')
    
    if (payment === 'success') {
      toast.success('¡Pago exitoso! Tu saldo se actualizará en unos momentos.')
      // Refresh balance
      setTimeout(() => {
        fetchBalance()
        fetchTransactions()
      }, 2000)
      // Clean URL
      window.history.replaceState({}, '', '/wallet')
    } else if (payment === 'failure') {
      toast.error('El pago fue rechazado. Intenta nuevamente.')
      window.history.replaceState({}, '', '/wallet')
    } else if (payment === 'pending') {
      toast('Pago pendiente. Te notificaremos cuando se complete.', { icon: '⏳' })
      window.history.replaceState({}, '', '/wallet')
    }
  }, [fetchBalance, fetchTransactions])

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount)
    if (!amount || amount <= 0) {
      toast.error('Ingresa un monto válido')
      return
    }
    if (amount < 50) {
      toast.error('El monto mínimo es de $50 MXN')
      return
    }
    if (amount > 10000) {
      toast.error('El monto máximo es de $10,000 MXN')
      return
    }
    if (depositMethod === 'clabe') {
      // Registrar solicitud de depósito por transferencia
      const result = await requestDeposit(amount, 'clabe')
      if (result.success) {
        toast.success('Sigue las instrucciones para completar la transferencia.')
        setShowDepositModal(false)
        // Agregar movimiento pending al historial
        setClabeTransactions(prev => [
          {
            id: `pending-${Date.now()}`,
            type: 'deposit',
            method: 'clabe',
            amount,
            status: 'pending',
            description: 'Depósito por transferencia CLABE',
            createdAt: new Date(),
            reference: '',
            rejectionReason: '',
          },
          ...prev
        ])
      }
    }
  }

  const canWithdraw = () => {
    if (!userData) return { allowed: false, reason: 'No se pudo cargar tu información' }
    if (!balance || balance.winnings < 100) return { allowed: false, reason: 'Necesitas al menos $100 en saldo ganado' }
    if (!userData.emailVerified) return { allowed: false, reason: 'Debes verificar tu email' }
    if (!userData.phoneVerified) return { allowed: false, reason: 'Debes verificar tu teléfono' }
    if (!userData.kyc?.verified) return { allowed: false, reason: 'Tu verificación KYC debe estar aprobada' }
    return { allowed: true }
  }

  const handleWithdrawalClick = () => {
    const validation = canWithdraw()
    if (!validation.allowed) {
      toast.error(validation.reason)
      return
    }
    setShowWithdrawalModal(true)
  }

  const handleWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount)
    
    // Validaciones
    if (!amount || amount <= 0) {
      toast.error('Ingresa un monto válido')
      return
    }
    if (amount < 100) {
      toast.error('El monto mínimo de retiro es $100 MXN')
      return
    }
    if (amount > balance.winnings) {
      toast.error('No puedes retirar más de tu saldo ganado')
      return
    }
    if (!clabeDestination || clabeDestination.length !== 18) {
      toast.error('Ingresa una CLABE válida de 18 dígitos')
      return
    }
    if (!beneficiaryName || beneficiaryName.trim().length < 3) {
      toast.error('Ingresa el nombre del beneficiario')
      return
    }

    const result = await requestWithdrawal(amount, clabeDestination, beneficiaryName)
    if (result.success) {
      toast.success('Solicitud de retiro enviada. Será procesada en 1-3 días hábiles.')
      setShowWithdrawalModal(false)
      setWithdrawalAmount('')
      setClabeDestination('')
      setBeneficiaryName('')
      fetchBalance()
      fetchTransactions()
    }
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <TrendingDown className="text-kiniela-muted" size={20} />
      case 'withdrawal':
        return <TrendingUp className="text-kiniela-cta" size={20} />
      case 'bet':
        return <DollarSign className="text-kiniela-gold" size={20} />
      case 'win':
        return <TrendingUp className="text-kiniela-success" size={20} />
      default:
        return <Clock className="text-kiniela-text" size={20} />
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-kiniela-bg min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-kiniela-gold">Mi Billetera</h1>

      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card bg-kiniela-card border border-kiniela-gold">
          <h3 className="text-sm font-medium mb-2 text-kiniela-muted">Saldo Total</h3>
          <p className="text-4xl font-bold text-kiniela-gold">${balance?.total?.toFixed(2) || '0.00'}</p>
        </div>

        <div className="card bg-kiniela-card border border-kiniela-border">
          <h3 className="text-sm font-medium text-kiniela-muted mb-2">Saldo Depositado</h3>
          <p className="text-3xl font-bold text-kiniela-text">${balance?.deposited?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-kiniela-muted mt-2">No retirable</p>
        </div>

        <div className="card bg-kiniela-card border border-kiniela-border">
          <h3 className="text-sm font-medium text-kiniela-muted mb-2">Saldo Ganado</h3>
          <p className="text-3xl font-bold text-kiniela-success">${balance?.winnings?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-kiniela-muted mt-2">Retirable</p>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card bg-kiniela-card border border-kiniela-border">
          <h2 className="text-xl font-bold mb-4 text-kiniela-gold">Depositar</h2>
          <p className="text-kiniela-text mb-4">Agrega saldo para participar en quinielas</p>
          <button 
            className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover w-full"
            onClick={() => setShowDepositModal(true)}
          >
            Depositar Dinero
          </button>
        </div>

        <div className="card bg-kiniela-card border border-kiniela-border">
          <h2 className="text-xl font-bold mb-4 text-kiniela-gold">Retirar</h2>
          <p className="text-kiniela-text mb-4">Retira tus ganancias (mínimo $100)</p>
          <button 
            className="btn bg-kiniela-accent text-kiniela-text hover:bg-kiniela-accentHover w-full disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleWithdrawalClick}
          >
            Retirar Dinero
          </button>
        </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-kiniela-card border border-kiniela-border rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-kiniela-gold">Depositar Dinero</h2>
              <button
                onClick={() => setShowDepositModal(false)}
                className="text-kiniela-text hover:text-kiniela-gold"
              >
                <X size={24} />
              </button>
            </div>
            {/* Medios de pago aceptados (siempre visible) */}
            <div className="mb-4">
              <div className="bg-kiniela-accent/20 border border-kiniela-border rounded-lg p-4">
                <p className="text-sm text-kiniela-text">
                  <strong>💳 Medios de pago aceptados:</strong><br />
                  • Transferencia CLABE
                </p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-kiniela-text mb-2">
                Monto a depositar (MXN)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-kiniela-text">$</span>
                <input
                  type="number"
                  min="50"
                  max="10000"
                  step="50"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(sanitizeNumber(e.target.value))}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-kiniela-border rounded-lg focus:ring-2 focus:ring-kiniela-gold focus:border-transparent bg-kiniela-bg text-kiniela-text"
                />
              </div>
              <p className="mt-2 text-sm text-kiniela-muted">
                Mínimo: $50 MXN | Máximo: $10,000 MXN
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[100, 200, 500].map((amount) => (
                <button
                  key={amount}
                  onClick={() => setDepositAmount(amount.toString())}
                  className="py-2 px-4 border border-kiniela-border rounded-lg hover:bg-kiniela-accent hover:border-kiniela-gold transition text-kiniela-text"
                >
                  ${amount}
                </button>
              ))}
            </div>
            {/* Método de pago */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-kiniela-text mb-2">Método de pago</label>
              <div className="flex space-x-2">
                <button
                  className="btn btn-sm bg-kiniela-cta text-kiniela-text"
                  disabled
                >
                  Transferencia CLABE
                </button>
              </div>
            </div>




            {/* Instrucciones CLABE */}
            {depositMethod === 'clabe' && (
              <div className="mb-6 bg-kiniela-gold/20 border border-kiniela-gold rounded-lg p-4">
                <p className="text-sm text-kiniela-gold mb-2 font-bold">
                  💸 Instrucciones para depósito por CLABE:
                </p>
                <ol className="text-sm text-kiniela-text list-decimal pl-5 mb-2 space-y-1">
                  <li>Elige el monto que deseas depositar.</li>
                  <li>Copia el número CLABE:</li>
                  <div className="flex items-center gap-2 my-2 ml-2">
                    <span className="font-mono text-lg select-all text-kiniela-gold">722969010377931842</span>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs bg-kiniela-gold text-kiniela-bg rounded hover:bg-[#C19F2E] border border-kiniela-gold"
                      onClick={() => {
                        navigator.clipboard.writeText('722969010377931842')
                        toast.success('CLABE copiada')
                      }}
                    >
                      Copiar CLABE
                    </button>
                  </div>
                  <li>Presiona el botón de <strong>SOLICITAR</strong>.</li>
                  <li>Ve a tu banca en línea o app y realiza una transferencia a la cuenta CLABE que copiaste. <br />Agrega el <strong>nombre que pusiste en la plataforma</strong> como referencia.</li>
                  <li>Espera a que Kiniela valide tu pago (puede tardar hasta 24 horas).</li>
                  <li>Una vez validado tu pago, tu saldo será agregado a tu cuenta.</li>
                </ol>
                <span className="text-xs text-kiniela-muted">No hay comisión.</span>
              </div>
            )}

            {/* (El bloque de medios de pago ya está arriba, y la comisión ya se muestra) */}

          

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDepositModal(false)}
                className="flex-1 btn bg-kiniela-card text-kiniela-text hover:bg-[#333333] border border-kiniela-border"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeposit}
                className="flex-1 btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover"
                disabled={
                  loading || !depositAmount ||
                  (depositMethod === 'clabe' && clabeTransactions.some(tx => tx.status === 'pending' || tx.status === 'en espera' || tx.status === 'validating'))
                }
              >
                {loading ? 'Procesando...' : depositMethod === 'clabe' ? 'Solicitar' : 'Continuar'}
              </button>
            </div>
            {depositMethod === 'clabe' && clabeTransactions.some(tx => tx.status === 'pending' || tx.status === 'en espera' || tx.status === 'validating') && (
              <p className="mt-2 text-xs text-kiniela-cta text-center">
                Ya tienes un depósito por transferencia CLABE pendiente de validación. Espera a que sea aceptado o cancelado antes de registrar otro.
              </p>
            )}


            {depositMethod === 'clabe' && (
              <p className="mt-4 text-xs text-kiniela-muted text-center">
                Recuerda usar tu nombre completo como referencia en la transferencia. El saldo se acreditará tras validación manual.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-kiniela-card border border-kiniela-border rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-kiniela-gold">Retirar Dinero</h2>
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="text-kiniela-text hover:text-kiniela-gold"
              >
                <X size={24} />
              </button>
            </div>

            {/* Requisitos cumplidos */}
            <div className="mb-4 bg-kiniela-success/20 border border-kiniela-success rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle2 size={16} className="text-kiniela-success mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-kiniela-success">Verificaciones completas</p>
                  <p className="text-xs text-kiniela-text mt-1">
                    ✓ Email verificado<br />
                    ✓ Teléfono verificado<br />
                    ✓ KYC aprobado
                  </p>
                </div>
              </div>
            </div>

            {/* Saldo disponible */}
            <div className="mb-6 bg-kiniela-accent/20 border border-kiniela-border rounded-lg p-4">
              <p className="text-sm text-kiniela-muted">Saldo disponible para retiro</p>
              <p className="text-2xl font-bold text-kiniela-success">${balance?.winnings?.toFixed(2) || '0.00'}</p>
            </div>

            {/* Monto a retirar */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-kiniela-text mb-2">
                Monto a retirar (MXN)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-kiniela-text">$</span>
                <input
                  type="number"
                  min="100"
                  max={balance?.winnings || 0}
                  step="10"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(sanitizeNumber(e.target.value))}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-kiniela-border rounded-lg focus:ring-2 focus:ring-kiniela-gold focus:border-transparent bg-kiniela-bg text-kiniela-text"
                />
              </div>
              <p className="mt-2 text-sm text-kiniela-muted">
                Mínimo: $100 MXN | Máximo: ${balance?.winnings?.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* CLABE destino */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-kiniela-text mb-2">
                CLABE bancaria destino
              </label>
              <input
                type="text"
                maxLength="18"
                value={clabeDestination}
                onChange={(e) => setClabeDestination(e.target.value.replace(/\D/g, ''))}
                placeholder="18 dígitos"
                className="w-full px-4 py-3 border border-kiniela-border rounded-lg focus:ring-2 focus:ring-kiniela-gold focus:border-transparent bg-kiniela-bg text-kiniela-text font-mono"
              />
              <p className="mt-1 text-xs text-kiniela-muted">
                {clabeDestination.length}/18 dígitos
              </p>
            </div>

            {/* Nombre del beneficiario */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-kiniela-text mb-2">
                Nombre del beneficiario
              </label>
              <input
                type="text"
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder="Nombre completo del titular de la cuenta"
                className="w-full px-4 py-3 border border-kiniela-border rounded-lg focus:ring-2 focus:ring-kiniela-gold focus:border-transparent bg-kiniela-bg text-kiniela-text"
              />
            </div>

            {/* Información importante */}
            <div className="mb-6 bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-yellow-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-500">Tiempo de procesamiento</p>
                  <p className="text-xs text-kiniela-text mt-1">
                    Los retiros son procesados manualmente en un plazo de 1 a 3 días hábiles.
                    Asegúrate de que los datos bancarios sean correctos.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="flex-1 btn bg-kiniela-card text-kiniela-text hover:bg-[#333333] border border-kiniela-border"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleWithdrawal}
                className="flex-1 btn bg-kiniela-accent text-kiniela-text hover:bg-kiniela-accentHover"
                disabled={loading}
              >
                {loading ? 'Procesando...' : 'Solicitar Retiro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="card bg-kiniela-card border border-kiniela-border">
        <h2 className="text-2xl font-bold mb-6 text-kiniela-gold">Historial de Transacciones</h2>
        {/* Historial de todas transacciones */}
        {transactions.length === 0 && clabeTransactions.length === 0 ? (
          <div className="text-center py-12 text-kiniela-text">
            <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
            <p>No hay transacciones aún</p>
          </div>
        ) : (
          <div className="space-y-3">
            {[...clabeTransactions, ...transactions].sort((a, b) => {
              const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt?.toDate?.() || new Date(0)
              const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt?.toDate?.() || new Date(0)
              return dateB - dateA
            }).map((transaction) => {
              // Visual para CLABE
              let rowClass = 'border-kiniela-border hover:bg-kiniela-bg';
              let amountClass = 'text-kiniela-success';
              if (transaction.method === 'clabe') {
                if (transaction.status === 'pending') {
                  rowClass = 'border-kiniela-gold bg-kiniela-gold/20';
                  amountClass = 'text-kiniela-gold';
                } else if (transaction.status === 'approved') {
                  // Mismo estilo que depósito Mercado Pago
                  rowClass = 'border-kiniela-border hover:bg-kiniela-bg';
                  amountClass = 'text-kiniela-success';
                } else if (transaction.status === 'rejected') {
                  rowClass = 'border-kiniela-cta bg-kiniela-cta/20';
                  amountClass = 'text-kiniela-cta';
                }
              } else {
                if (['deposit', 'win', 'prize'].includes(transaction.type)) {
                  amountClass = 'text-kiniela-success';
                } else {
                  amountClass = 'text-kiniela-cta';
                }
              }
              return (
                <div
                  key={transaction.id}
                  className={`flex items-center justify-between p-4 border rounded-lg transition ${rowClass}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-kiniela-bg rounded-full">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div>
                      <p className="font-medium text-kiniela-text">{transaction.description || transaction.type}</p>
                      {transaction.method === 'clabe' && (
                        <p className="text-sm text-kiniela-text">Referencia: {transaction.reference || 'Sin referencia'}</p>
                      )}
                      <p className="text-sm text-kiniela-text">
                        {transaction.createdAt && (transaction.createdAt instanceof Date
                          ? format(transaction.createdAt, 'PPP p', { locale: es })
                          : format(transaction.createdAt.toDate(), 'PPP p', { locale: es }))}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${amountClass}`}>
                      {['deposit', 'win', 'prize'].includes(transaction.type) ? '+' : '-'}
                      ${transaction.amount?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-kiniela-text capitalize">{transaction.status}</p>
                    {transaction.status === 'rejected' && transaction.method === 'clabe' && (
                      <p className="text-xs text-kiniela-cta">Motivo: {transaction.rejectionReason}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  )
}
